/**
 * Placement Module — 3 tabs:
 *  • Overview          — every CRM-linked student with their placement fields
 *  • Data Quality      — students with Placement_Status set but company/CTC/date blank
 *  • Auto-Flag Console — preview & apply Fit_to_Placement to eligible students
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Briefcase, RefreshCw, AlertCircle, CheckCircle2, ExternalLink, Play, Search, ChevronDown, ChevronRight, Phone, Mail, GraduationCap, CalendarDays, Receipt, FileText, Users, X, Download, SlidersHorizontal, TrendingUp, Building2, Filter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import api from '../../lib/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = (iso.split('T')[0] || '').split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${String(d).padStart(2, '0')}-${MONTHS[m - 1]}-${y}`;
}
const CRM_ORG_ID = '690252559';
const crmLink = (leadId) => leadId ? `https://crm.zoho.com/crm/org${CRM_ORG_ID}/tab/Leads/${leadId}` : null;

export default function PlacementPage() {
  const [candidateId, setCandidateId] = useState(null);
  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="max-w-[1500px] mx-auto px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Placement Module</h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">CRM-linked placement tracker, data quality, and Fit-to-Placement automation</p>
          </div>
        </div>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList>
            <TabsTrigger value="overview" data-testid="placement-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="data-quality" data-testid="placement-tab-data-quality">Data Quality</TabsTrigger>
            <TabsTrigger value="auto-flag" data-testid="placement-tab-auto-flag">Auto-Flag Console</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4"><OverviewTab onOpenCandidate={setCandidateId} /></TabsContent>
          <TabsContent value="data-quality" className="mt-4"><DataQualityTab onOpenCandidate={setCandidateId} /></TabsContent>
          <TabsContent value="auto-flag" className="mt-4"><AutoFlagTab onOpenCandidate={setCandidateId} /></TabsContent>
        </Tabs>
      </div>
      {candidateId && <CandidateDrilldown studentId={candidateId} onClose={() => setCandidateId(null)} />}
    </div>
  );
}

// ─── KPI definitions: id → {label, color, predicate(row)} ─────────────
const KPI_DEFS = [
  { id: 'all',      label: 'Total Candidates', tone: 'slate',   icon: Users,        test: () => true },
  { id: 'fit',      label: 'Fit to Placement', tone: 'emerald', icon: CheckCircle2, test: (r) => r.fit_to_placement === true },
  { id: 'placed',   label: 'Placed',           tone: 'teal',    icon: Briefcase,    test: (r) => ['Done', 'Placed'].includes(r.placement_status) },
  { id: 'inprog',   label: 'In-Progress',      tone: 'violet',  icon: TrendingUp,   test: (r) => ['Pending', 'In-Progress', 'In Progress'].includes(r.placement_status) },
  { id: 'awaiting', label: 'Awaiting 5 Jobs',  tone: 'amber',   icon: AlertCircle,  test: (r) => r.five_job_opportunities_given !== 'Yes' && (r.course_completion_pct ?? 0) >= 50 },
  { id: 'dq',       label: 'Data Quality',     tone: 'rose',    icon: AlertCircle,  test: (r) => ['Done', 'Placed'].includes(r.placement_status) && (!r.placed_company_name || !r.placed_company_ctc || !r.placement_date || !r.placement_company_job_title) },
  { id: 'course50', label: 'Course ≥ 50 %',    tone: 'sky',     icon: GraduationCap, test: (r) => (r.course_completion_pct ?? 0) >= 50 },
  { id: 'noCrm',    label: 'No CRM Lead',      tone: 'amber',   icon: AlertCircle,  test: (r) => r.has_crm_lead === false },
];
const TONE_BG = { slate: 'bg-slate-50 border-slate-200 hover:bg-slate-100', emerald: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100', teal: 'bg-teal-50 border-teal-200 hover:bg-teal-100', violet: 'bg-violet-50 border-violet-200 hover:bg-violet-100', amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100', rose: 'bg-rose-50 border-rose-200 hover:bg-rose-100', sky: 'bg-sky-50 border-sky-200 hover:bg-sky-100' };
const TONE_TEXT = { slate: 'text-slate-700', emerald: 'text-emerald-700', teal: 'text-teal-700', violet: 'text-violet-700', amber: 'text-amber-700', rose: 'text-rose-700', sky: 'text-sky-700' };
const COMPLETION_BUCKETS = [
  { id: '0',     label: 'Not started (0 %)',  test: (v) => v === 0 || v === null || v === undefined },
  { id: '1-49',  label: '1 – 49 %',           test: (v) => v != null && v > 0 && v < 50 },
  { id: '50-79', label: '50 – 79 %',          test: (v) => v != null && v >= 50 && v < 80 },
  { id: '80-99', label: '80 – 99 %',          test: (v) => v != null && v >= 80 && v < 100 },
  { id: '100',   label: 'Completed (100 %)',  test: (v) => v != null && v >= 100 },
];

function MultiSelectChip({ label, options, selected, onChange, testId }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const count = selected.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-[11px] font-normal" data-testid={testId}>
          {label}{count > 0 && <Badge variant="outline" className="ml-1.5 text-[9px] bg-violet-50 text-violet-700 border-violet-200">{count}</Badge>}
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 max-h-80 overflow-auto" align="start">
        {options.length === 0 ? <p className="text-[10px] italic text-slate-400">No options</p> : options.map(opt => (
          <label key={opt} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="rounded text-violet-600" />
            <span className="text-[11px]">{opt}</span>
          </label>
        ))}
        {selected.length > 0 && (
          <button onClick={() => onChange([])} className="mt-1 text-[10px] text-violet-600 hover:underline px-2">Clear</button>
        )}
      </PopoverContent>
    </Popover>
  );
}

const TRISTATE = [{ id: 'all', label: 'All' }, { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }];
function TriToggle({ label, value, onChange, testId }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{label}</span>
      <div className="inline-flex rounded border border-slate-200 overflow-hidden">
        {TRISTATE.map(opt => (
          <button key={opt.id} onClick={() => onChange(opt.id)} data-testid={`${testId}-${opt.id}`}
            className={`text-[10px] px-2 h-7 transition ${value === opt.id ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>{opt.label}</button>
        ))}
      </div>
    </div>
  );
}

const EMPTY_FILTERS = {
  q: '',
  kpi: 'all',
  placement_status: [],
  fit: 'all',
  completion: [],
  course: [],
  centre: [],
  salesperson: [],
  trainer: [],
  batch: [],
  company: '',
  ctc_min: '',
  ctc_max: '',
  placement_from: '',
  placement_to: '',
  five_jobs: 'all',
  onboarding: [],
  outstanding: 'all',
  student_status: [],
  include_blacklisted: false,
  group_by_batch: false,
};

function OverviewTab({ onOpenCandidate }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [flagging, setFlagging] = useState(null); // student_id currently being flagged

  const handleManualFlag = async (r, e) => {
    e?.stopPropagation();
    const msg = r.low_attendance_warning
      ? `Manually flag ${r.name} (${r.enrollment_no}) as Fit_to_Placement = Yes in Zoho CRM?\n\n⚠️ Low attendance: ${r.attendance_pct_active_batch}% (auto-flag would have skipped this).\n\nProceed?`
      : `Flag ${r.name} (${r.enrollment_no}) as Fit_to_Placement = Yes in Zoho CRM?`;
    if (!window.confirm(msg)) return;
    setFlagging(r.student_id);
    try {
      const res = await api.post(`/placement/manual-flag/${r.student_id}`);
      if (res.data?.status === 'success') {
        toast.success(`${r.name} flagged as Fit ✓`);
        load();
      } else {
        toast.error(`Failed: ${res.data?.message || 'CRM update failed'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to flag');
    } finally { setFlagging(null); }
  };

  const load = () => { setLoading(true); api.get('/placement/overview').then(r => setData(r.data.rows || [])).finally(() => setLoading(false)); };
  const loadSyncStatus = () => api.get('/placement/sync-status').then(r => setLastSync(r.data));
  useEffect(() => { load(); loadSyncStatus(); }, []);

  const triggerSync = async () => {
    if (!window.confirm(`Sync placement fields from Zoho CRM for ${data.length} students? Uses ~${Math.ceil(data.length / 100)} CRM API call${Math.ceil(data.length / 100) === 1 ? '' : 's'}.`)) return;
    setSyncing(true);
    try {
      const r = await api.post('/placement/sync-from-crm', {});
      toast.success(`Synced ${r.data.synced} / ${r.data.total} leads (${r.data.failed} failed)`);
      load(); loadSyncStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  // Distinct options
  const opts = useMemo(() => {
    const uniq = (key) => [...new Set(data.map(r => r[key]).filter(Boolean))].sort();
    return {
      placement_status: uniq('placement_status'),
      course: uniq('course'),
      centre: uniq('centre_name'),
      onboarding: uniq('job_basket_onboarding_status'),
      student_status: uniq('student_status'),
      salesperson: uniq('salesperson'),
      trainer: uniq('trainer_name'),
      batch: [...new Set(data.map(r => r.latest_batch_number).filter(Boolean))].sort((a, b) => Number(b) - Number(a)).map(String),
    };
  }, [data]);

  // KPI counts respect blacklist toggle (blacklisted always excluded unless include_blacklisted=true)
  const visibleForKpi = useMemo(() => filters.include_blacklisted ? data : data.filter(r => r.placement_status !== 'Blacklisted'), [data, filters.include_blacklisted]);

  const kpiCounts = useMemo(() => {
    const out = {};
    for (const k of KPI_DEFS) out[k.id] = visibleForKpi.filter(k.test).length;
    return out;
  }, [visibleForKpi]);

  // Apply filters
  const filtered = useMemo(() => {
    const kpiDef = KPI_DEFS.find(k => k.id === filters.kpi) || KPI_DEFS[0];
    const q = filters.q.trim().toLowerCase();
    const company = filters.company.trim().toLowerCase();
    const ctcMin = filters.ctc_min ? Number(filters.ctc_min) : null;
    const ctcMax = filters.ctc_max ? Number(filters.ctc_max) : null;
    return data.filter(r => {
      // Default-exclude Blacklisted unless explicitly included OR user filtered by blacklist
      const filteringBlacklist = filters.placement_status.includes('Blacklisted');
      if (!filters.include_blacklisted && !filteringBlacklist && r.placement_status === 'Blacklisted') return false;
      if (!kpiDef.test(r)) return false;
      if (q && !((r.enrollment_no || '').toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q))) return false;
      if (filters.placement_status.length && !filters.placement_status.includes(r.placement_status)) return false;
      if (filters.fit === 'yes' && r.fit_to_placement !== true) return false;
      if (filters.fit === 'no' && r.fit_to_placement === true) return false;
      if (filters.completion.length) {
        const v = r.course_completion_pct;
        const hit = filters.completion.some(id => COMPLETION_BUCKETS.find(b => b.id === id)?.test(v));
        if (!hit) return false;
      }
      if (filters.course.length && !filters.course.includes(r.course)) return false;
      if (filters.centre.length && !filters.centre.includes(r.centre_name)) return false;
      if (filters.salesperson.length && !filters.salesperson.includes(r.salesperson)) return false;
      if (filters.trainer.length && !filters.trainer.includes(r.trainer_name)) return false;
      if (filters.batch.length && !filters.batch.includes(String(r.latest_batch_number ?? ''))) return false;
      if (company && !(r.placed_company_name || '').toLowerCase().includes(company)) return false;
      const ctc = Number(r.placed_company_ctc) || 0;
      if (ctcMin != null && ctc < ctcMin) return false;
      if (ctcMax != null && ctc > ctcMax) return false;
      if (filters.placement_from && (!r.placement_date || r.placement_date < filters.placement_from)) return false;
      if (filters.placement_to && (!r.placement_date || r.placement_date > filters.placement_to)) return false;
      if (filters.five_jobs === 'yes' && r.five_job_opportunities_given !== 'Yes') return false;
      if (filters.five_jobs === 'no' && r.five_job_opportunities_given === 'Yes') return false;
      if (filters.onboarding.length && !filters.onboarding.includes(r.job_basket_onboarding_status)) return false;
      if (filters.outstanding === 'yes' && !(Number(r.due_amount) > 0)) return false;
      if (filters.outstanding === 'no' && Number(r.due_amount) > 0) return false;
      if (filters.student_status.length && !filters.student_status.includes(r.student_status)) return false;
      return true;
    });
  }, [data, filters]);

  // Group by batch: produce rows array with optional `__header` separator rows
  const displayRows = useMemo(() => {
    if (!filters.group_by_batch) return filtered.map(r => ({ kind: 'row', row: r }));
    const grouped = [...filtered].sort((a, b) => {
      const an = Number(a.latest_batch_number) || 0;
      const bn = Number(b.latest_batch_number) || 0;
      return bn - an;
    });
    const out = [];
    let lastBatch = '__init__';
    for (const r of grouped) {
      const key = r.latest_batch_number ?? '__none__';
      if (key !== lastBatch) {
        const sameBatch = grouped.filter(x => (x.latest_batch_number ?? '__none__') === key);
        out.push({ kind: 'header', batch_number: r.latest_batch_number, batch_name: r.latest_batch_name, trainer_name: r.trainer_name, count: sameBatch.length, centre_name: r.centre_name, course: r.course });
        lastBatch = key;
      }
      out.push({ kind: 'row', row: r });
    }
    return out;
  }, [filtered, filters.group_by_batch]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (filters.kpi !== 'all') c++;
    if (filters.placement_status.length) c++;
    if (filters.fit !== 'all') c++;
    if (filters.completion.length) c++;
    if (filters.course.length) c++;
    if (filters.centre.length) c++;
    if (filters.salesperson.length) c++;
    if (filters.trainer.length) c++;
    if (filters.batch.length) c++;
    if (filters.company) c++;
    if (filters.ctc_min || filters.ctc_max) c++;
    if (filters.placement_from || filters.placement_to) c++;
    if (filters.five_jobs !== 'all') c++;
    if (filters.onboarding.length) c++;
    if (filters.outstanding !== 'all') c++;
    if (filters.student_status.length) c++;
    return c;
  }, [filters]);

  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const exportCsv = () => {
    const cols = ['enrollment_no', 'name', 'course', 'centre_name', 'salesperson', 'trainer_name', 'latest_batch_number', 'latest_batch_name', 'student_status', 'course_completion_pct', 'has_crm_lead', 'fit_to_placement', 'placement_status', 'placed_company_name', 'placement_company_job_title', 'placed_company_ctc', 'placement_date', 'five_job_opportunities_given', 'job_basket_onboarding_status', 'due_amount'];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [cols.join(','), ...filtered.map(r => cols.map(c => escape(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `placement-overview-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows to CSV`);
  };

  // Avg CTC of placed (within current filter)
  const avgCtcPlaced = useMemo(() => {
    const placed = filtered.filter(r => ['Done', 'Placed'].includes(r.placement_status) && Number(r.placed_company_ctc) > 0);
    if (!placed.length) return null;
    return Math.round(placed.reduce((s, r) => s + Number(r.placed_company_ctc), 0) / placed.length);
  }, [filtered]);

  return (
    <div className="space-y-3">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {KPI_DEFS.map(k => {
          const Icon = k.icon;
          const active = filters.kpi === k.id;
          return (
            <button key={k.id} onClick={() => setFilters(f => ({ ...f, kpi: k.id }))} data-testid={`kpi-${k.id}`}
              className={`p-3 rounded-lg border text-left transition ${TONE_BG[k.tone]} ${active ? 'ring-2 ring-violet-500 shadow-sm' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <Icon className={`w-3.5 h-3.5 ${TONE_TEXT[k.tone]}`} />
                <span className={`text-[9px] uppercase tracking-wider font-bold ${TONE_TEXT[k.tone]}`}>{active ? 'Active' : ''}</span>
              </div>
              <p className={`text-xl font-bold ${TONE_TEXT[k.tone]} tabular-nums`}>{kpiCounts[k.id]}</p>
              <p className="text-[10px] text-slate-600 font-medium leading-tight mt-0.5">{k.label}</p>
              {k.id === 'placed' && avgCtcPlaced !== null && (
                <p className="text-[9px] text-teal-600 mt-0.5">Avg ₹{Number(avgCtcPlaced).toLocaleString('en-IN')}</p>
              )}
            </button>
          );
        })}
      </div>

      <Card className="p-3">
        {/* Filter strip — quick filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} placeholder="Enrol / name…" className="h-8 text-xs pl-7" data-testid="overview-search" />
          </div>
          <MultiSelectChip label="Status" options={opts.placement_status} selected={filters.placement_status} onChange={v => setFilters(f => ({ ...f, placement_status: v }))} testId="filter-status" />
          <MultiSelectChip label="Course" options={opts.course} selected={filters.course} onChange={v => setFilters(f => ({ ...f, course: v }))} testId="filter-course" />
          <MultiSelectChip label="Centre" options={opts.centre} selected={filters.centre} onChange={v => setFilters(f => ({ ...f, centre: v }))} testId="filter-centre" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-[11px] font-normal" data-testid="filter-completion">
                Completion{filters.completion.length > 0 && <Badge variant="outline" className="ml-1.5 text-[9px] bg-violet-50 text-violet-700 border-violet-200">{filters.completion.length}</Badge>}
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start">
              {COMPLETION_BUCKETS.map(b => (
                <label key={b.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                  <input type="checkbox" checked={filters.completion.includes(b.id)} onChange={() => setFilters(f => ({ ...f, completion: f.completion.includes(b.id) ? f.completion.filter(x => x !== b.id) : [...f.completion, b.id] }))} className="rounded text-violet-600" />
                  <span className="text-[11px]">{b.label}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>
          <MultiSelectChip label="Salesperson" options={opts.salesperson} selected={filters.salesperson} onChange={v => setFilters(f => ({ ...f, salesperson: v }))} testId="filter-salesperson" />
          <MultiSelectChip label="Trainer" options={opts.trainer} selected={filters.trainer} onChange={v => setFilters(f => ({ ...f, trainer: v }))} testId="filter-trainer" />
          <MultiSelectChip label="Batch #" options={opts.batch} selected={filters.batch} onChange={v => setFilters(f => ({ ...f, batch: v }))} testId="filter-batch" />
          <Button variant="outline" size="sm" onClick={() => setShowAdvanced(s => !s)} className="h-8 text-[11px] font-normal" data-testid="toggle-advanced">
            <SlidersHorizontal className="w-3 h-3 mr-1" />Advanced
            {showAdvanced ? <ChevronDown className="w-3 h-3 ml-1" /> : <ChevronRight className="w-3 h-3 ml-1" />}
          </Button>
          <Button variant={filters.group_by_batch ? 'default' : 'outline'} size="sm" onClick={() => setFilters(f => ({ ...f, group_by_batch: !f.group_by_batch }))} className={`h-8 text-[11px] ${filters.group_by_batch ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`} data-testid="toggle-group-by-batch">
            <Users className="w-3 h-3 mr-1" />{filters.group_by_batch ? 'Grouped' : 'Group by Batch'}
          </Button>
          <Button variant={filters.include_blacklisted ? 'default' : 'outline'} size="sm" onClick={() => setFilters(f => ({ ...f, include_blacklisted: !f.include_blacklisted }))} className={`h-8 text-[11px] ${filters.include_blacklisted ? 'bg-slate-700 hover:bg-slate-800 text-white' : ''}`} data-testid="toggle-blacklisted">
            {filters.include_blacklisted ? 'Hide Blacklisted' : 'Include Blacklisted'}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 text-[11px] text-rose-600 hover:bg-rose-50" data-testid="reset-filters">
              <X className="w-3 h-3 mr-1" />Reset ({activeFilterCount})
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} className="h-8 text-[11px]" data-testid="export-csv"><Download className="w-3 h-3 mr-1" />CSV</Button>
            <Button variant="outline" size="sm" onClick={load} className="h-8 text-[11px]" data-testid="overview-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
            <Button size="sm" onClick={triggerSync} disabled={syncing} className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700" data-testid="overview-sync-crm">
              <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing…' : 'Sync CRM'}
            </Button>
          </div>
        </div>

        {/* Advanced filters */}
        {showAdvanced && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3" data-testid="advanced-filters">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Company name</label>
              <Input value={filters.company} onChange={e => setFilters(f => ({ ...f, company: e.target.value }))} placeholder="Search company…" className="h-8 text-xs" data-testid="filter-company" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">CTC range (₹)</label>
              <div className="flex gap-1">
                <Input value={filters.ctc_min} onChange={e => setFilters(f => ({ ...f, ctc_min: e.target.value }))} placeholder="Min" type="number" className="h-8 text-xs" data-testid="filter-ctc-min" />
                <Input value={filters.ctc_max} onChange={e => setFilters(f => ({ ...f, ctc_max: e.target.value }))} placeholder="Max" type="number" className="h-8 text-xs" data-testid="filter-ctc-max" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Placement date</label>
              <div className="flex gap-1">
                <Input type="date" value={filters.placement_from} onChange={e => setFilters(f => ({ ...f, placement_from: e.target.value }))} className="h-8 text-xs" data-testid="filter-pd-from" />
                <Input type="date" value={filters.placement_to} onChange={e => setFilters(f => ({ ...f, placement_to: e.target.value }))} className="h-8 text-xs" data-testid="filter-pd-to" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5"><TriToggle label="Fit to Placement" value={filters.fit} onChange={v => setFilters(f => ({ ...f, fit: v }))} testId="filter-fit" /></div>
            <div className="flex flex-col gap-1.5"><TriToggle label="5 Job Opportunities" value={filters.five_jobs} onChange={v => setFilters(f => ({ ...f, five_jobs: v }))} testId="filter-5jobs" /></div>
            <div className="flex flex-col gap-1.5"><TriToggle label="Has Outstanding Dues" value={filters.outstanding} onChange={v => setFilters(f => ({ ...f, outstanding: v }))} testId="filter-outstanding" /></div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">JB Onboarding Status</label>
              <MultiSelectChip label="Onboarding" options={opts.onboarding} selected={filters.onboarding} onChange={v => setFilters(f => ({ ...f, onboarding: v }))} testId="filter-onboarding" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Student Account Status</label>
              <MultiSelectChip label="Account" options={opts.student_status} selected={filters.student_status} onChange={v => setFilters(f => ({ ...f, student_status: v }))} testId="filter-account-status" />
            </div>
          </div>
        )}

        {/* Result summary */}
        <div className="flex items-center justify-between mt-3 text-[10px] text-slate-500">
          <span><strong className="text-slate-700">{filtered.length}</strong> of {data.length} students{activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied` : ''}</span>
          {lastSync?.finished_at && <span className="text-[9px] text-slate-400">Last CRM sync: {fmtDate(lastSync.finished_at)} · {lastSync.synced}/{lastSync.total}</span>}
        </div>
      </Card>

      {loading ? <p className="text-xs text-slate-500 italic py-6 text-center">Loading…</p> : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 360px)' }}>
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Enrol</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Name</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Course</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Centre</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Batch #</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Trainer</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Salesperson</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Course Completion</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Fit to Placement</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Placement Status</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Company</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Job Title</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">CTC</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Placement Date</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">CRM</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-slate-600 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((item, i) => {
                  if (item.kind === 'header') {
                    return (
                      <tr key={`h-${i}`} className="bg-violet-50/70" data-testid={`group-header-${item.batch_number ?? 'unbatched'}`}>
                        <td colSpan={16} className="px-3 py-1.5">
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="font-mono px-1.5 py-0.5 bg-violet-100 text-violet-800 rounded font-bold">#{item.batch_number ?? '—'}</span>
                            <span className="font-semibold text-slate-800">{item.batch_name || 'Unbatched'}</span>
                            {item.trainer_name && <span className="text-slate-500">· Trainer: {item.trainer_name}</span>}
                            {item.centre_name && <span className="text-slate-500">· {item.centre_name}</span>}
                            {item.course && <span className="text-slate-500">· {item.course}</span>}
                            <span className="ml-auto text-[10px] font-medium text-violet-700">{item.count} student{item.count === 1 ? '' : 's'}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const r = item.row;
                  return (
                    <tr key={`r-${i}`} className="border-b border-slate-100 hover:bg-violet-50/30 cursor-pointer transition" onClick={() => onOpenCandidate?.(r.student_id)} data-testid={`candidate-row-${r.enrollment_no}`}>
                      <td className="px-3 py-1.5 font-mono">{r.enrollment_no}</td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1">
                          <span>{r.name}</span>
                          {!r.has_crm_lead && <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-700 border-amber-200 px-1 py-0">No CRM</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-[10px] text-slate-600">{r.course || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-[10px] text-slate-600">{r.centre_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-[10px] font-mono">{r.latest_batch_number ? `#${r.latest_batch_number}` : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-[10px] text-slate-600">{r.trainer_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-[10px] text-slate-600">{r.salesperson || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-xs tabular-nums">
                        {r.course_completion_pct == null ? <span className="text-slate-300">—</span> : (
                          <div className="flex items-center gap-1">
                            <span className={r.course_completion_pct >= 50 ? 'font-semibold text-emerald-700' : 'text-slate-600'}>{r.course_completion_pct}%</span>
                            {r.low_attendance_warning && (
                              <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-700 border-amber-200 px-1 py-0" title={`Low attendance: ${r.attendance_pct_active_batch}%`}>
                                <AlertCircle className="w-2.5 h-2.5 mr-0.5" />Att {r.attendance_pct_active_batch}%
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.fit_to_placement === true ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">
                            <CheckCircle2 className="w-3 h-3 mr-1" />Yes · {fmtDate(r.fit_to_placement_date)}
                          </Badge>
                        ) : <span className="text-slate-400 text-[10px]">—</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.placement_status ? <Badge variant="outline" className="text-[10px]">{r.placement_status}</Badge> : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5">{r.placed_company_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5">{r.placement_company_job_title || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.placed_company_ctc ? Number(r.placed_company_ctc).toLocaleString('en-IN') : '—'}</td>
                      <td className="px-3 py-1.5 font-mono">{fmtDate(r.placement_date)}</td>
                      <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                        {r.lead_id && (
                          <a href={crmLink(r.lead_id)} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline text-[10px] inline-flex items-center gap-1">
                            Open<ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                        {r.has_crm_lead && r.fit_to_placement !== true && (
                          <Button size="sm" variant="outline" className={`h-6 text-[10px] px-2 ${r.low_attendance_warning ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`} disabled={flagging === r.student_id} onClick={(e) => handleManualFlag(r, e)} data-testid={`manual-flag-${r.enrollment_no}`}>
                            {flagging === r.student_id ? '…' : (<><CheckCircle2 className="w-2.5 h-2.5 mr-1" />Flag Fit</>)}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {displayRows.length === 0 && <tr><td colSpan={16} className="text-center text-xs italic text-slate-400 py-6">No students match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function DataQualityTab({ onOpenCandidate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get('/placement/data-quality').then(r => setRows(r.data.rows || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">Students with <code className="px-1 bg-slate-100 rounded text-[10px]">Placement_Status = Done/Placed</code> or <code className="px-1 bg-slate-100 rounded text-[10px]">Five_Job_Opportunities_Given = Yes</code>, but with placement-detail fields still blank in CRM.</p>
        <Button variant="outline" size="sm" onClick={load} className="text-xs" data-testid="dq-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
      </div>
      {loading ? <p className="text-xs text-slate-500 italic py-6 text-center">Loading…</p> : rows.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-center">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
          <p className="text-sm font-semibold text-emerald-700">All clear!</p>
          <p className="text-xs text-emerald-600">Every placed/done student has their placement details filled.</p>
        </div>
      ) : (
        <div className="rounded border bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase">Enrol</TableHead>
                <TableHead className="text-[10px] uppercase">Name</TableHead>
                <TableHead className="text-[10px] uppercase">Placement Status</TableHead>
                <TableHead className="text-[10px] uppercase">5 Job Opp.</TableHead>
                <TableHead className="text-[10px] uppercase">Missing fields</TableHead>
                <TableHead className="text-[10px] uppercase">CRM</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} className="cursor-pointer hover:bg-violet-50/40" onClick={() => onOpenCandidate?.(r.student_id)} data-testid={`dq-row-${r.enrollment_no}`}>
                  <TableCell className="text-xs font-mono">{r.enrollment_no}</TableCell>
                  <TableCell className="text-xs font-medium">{r.name}</TableCell>
                  <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{r.placement_status || '—'}</Badge></TableCell>
                  <TableCell className="text-xs">{r.five_job_opportunities_given || '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.missing_fields.map(f => (
                        <span key={f} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[10px] border border-rose-200">{f}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <a href={crmLink(r.lead_id)} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline text-[10px] inline-flex items-center gap-1">
                      Open in CRM<ExternalLink className="w-3 h-3" />
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function AutoFlagTab({ onOpenCandidate }) {
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const loadPreview = () => { setLoading(true); api.get('/placement/fit-eligible-preview').then(r => setPreview(r.data)).finally(() => setLoading(false)); };
  const loadAudit = () => api.get('/placement/audit-log?limit=100').then(r => setAudit(r.data.rows || []));
  useEffect(() => { loadPreview(); loadAudit(); }, []);

  const apply = async () => {
    if (!preview?.rows?.length) return;
    if (!window.confirm(`Apply Fit_to_Placement = Yes to ${preview.rows.length} student${preview.rows.length === 1 ? '' : 's'} in Zoho CRM? This writes to the live CRM and cannot be auto-reverted.`)) return;
    setRunning(true);
    try {
      const r = await api.post('/placement/fit-flag-eligible', {});
      toast.success(`Done: ${r.data.succeeded} succeeded, ${r.data.failed} failed`);
      loadPreview(); loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-emerald-50/40 border-emerald-200">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0"><AlertCircle className="w-4 h-4 text-emerald-700" /></div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Eligibility rules (all must be true)</p>
            <ul className="text-[11px] text-slate-600 mt-1 list-disc list-inside space-y-0.5">
              <li>Batch is Running/Active and has reached <strong>≥ 50 % topic syllabus completion</strong></li>
              <li>Student's own attendance in that batch is <strong>≥ 50 %</strong></li>
              <li>Student's <code className="px-1 bg-slate-100 rounded">course_completion_status</code> is not Drop / Hold / Dropped / On Hold</li>
              <li>Student has a linked CRM lead</li>
              <li>Student has not already been flagged in the audit log</li>
            </ul>
            <p className="text-[10px] text-slate-500 italic mt-2">Auto-runs nightly at 02:00 IST. Use the button below for an on-demand preview & apply.</p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">Eligible Students Preview</p>
            <p className="text-[11px] text-slate-500">Students who match all rules and have not yet been flagged.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadPreview} disabled={loading} className="text-xs" data-testid="preview-refresh">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button size="sm" onClick={apply} disabled={running || !preview?.rows?.length} className="text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="apply-fit-flag">
              <Play className="w-3 h-3 mr-1" />{running ? 'Applying…' : `Apply to ${preview?.rows?.length || 0}`}
            </Button>
          </div>
        </div>
        {preview && preview.rows.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-6 text-center">No eligible students right now. Either no batches have crossed 50 % topic completion, or all eligible students are already flagged.</p>
        ) : (
          <div className="rounded border bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase">Enrol</TableHead>
                  <TableHead className="text-[10px] uppercase">Name</TableHead>
                  <TableHead className="text-[10px] uppercase">Batch</TableHead>
                  <TableHead className="text-[10px] uppercase">Trainer</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Topic %</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Attendance %</TableHead>
                  <TableHead className="text-[10px] uppercase">CRM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview?.rows || []).map((r, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-violet-50/40" onClick={() => onOpenCandidate?.(r.student_id)} data-testid={`af-row-${r.enrollment_no}`}>
                    <TableCell className="text-xs font-mono">{r.enrollment_no}</TableCell>
                    <TableCell className="text-xs font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs">#{r.batch_number} {r.batch_name}</TableCell>
                    <TableCell className="text-xs">{r.trainer_name}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{r.topic_completion_pct}%</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{r.attendance_pct}%</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <a href={crmLink(r.lead_id)} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline text-[10px] inline-flex items-center gap-1">Open<ExternalLink className="w-3 h-3" /></a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <p className="text-sm font-semibold text-slate-800 mb-2">Recent Audit Log</p>
        <div className="rounded border bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase">When</TableHead>
                <TableHead className="text-[10px] uppercase">Enrol</TableHead>
                <TableHead className="text-[10px] uppercase">Name</TableHead>
                <TableHead className="text-[10px] uppercase">Batch</TableHead>
                <TableHead className="text-[10px] uppercase">By</TableHead>
                <TableHead className="text-[10px] uppercase">Trigger</TableHead>
                <TableHead className="text-[10px] uppercase">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {audit.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-xs italic text-slate-400 py-6">No entries yet.</TableCell></TableRow>}
              {audit.map((a, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs font-mono">{fmtDate(a.set_at)}</TableCell>
                  <TableCell className="text-xs font-mono">{a.enrollment_no}</TableCell>
                  <TableCell className="text-xs">{a.name}</TableCell>
                  <TableCell className="text-xs">#{a.batch_number}</TableCell>
                  <TableCell className="text-xs">{a.set_by}</TableCell>
                  <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{a.trigger_source}</Badge></TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${a.status === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                      {a.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}


// ─── Helpers ───────────────────────────────────────────────────────────
const ZOHO_BOOKS_ORG = process.env.REACT_APP_ZOHO_BOOKS_ORG_ID || '';
const booksInvoiceLink = (invoiceId) => ZOHO_BOOKS_ORG && invoiceId ? `https://books.zoho.com/app/${ZOHO_BOOKS_ORG}#/invoices/${invoiceId}` : null;
const fmtMoney = (n) => (n === null || n === undefined) ? '—' : `₹${Number(n).toLocaleString('en-IN')}`;

function PctBar({ value }) {
  if (value === null || value === undefined) return <span className="text-slate-300 text-[10px]">—</span>;
  const v = Math.max(0, Math.min(100, value));
  const tone = v >= 70 ? 'bg-emerald-500' : v >= 30 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-[10px] font-mono tabular-nums w-9 text-right">{v.toFixed(0)}%</span>
    </div>
  );
}

function CandidateDrilldown({ studentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [collapsedBatches, setCollapsedBatches] = useState({});
  const [ticketOpen, setTicketOpen] = useState(false);
  const [topicDialog, setTopicDialog] = useState(null); // {batch_id, batch_label}
  const [flagging, setFlagging] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/placement/candidate/${studentId}`)
      .then(r => {
        setData(r.data);
        const init = {};
        (r.data.batches || []).forEach((b, idx) => {
          const isRunning = (b.batch_status || '').toLowerCase().includes('running');
          if (!isRunning && idx > 0) init[b.batch_id] = true;
        });
        setCollapsedBatches(init);
      })
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load candidate'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl"><p className="text-sm text-slate-500 italic py-10 text-center">Loading candidate…</p></DialogContent>
    </Dialog>
  );
  if (err || !data) return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md"><p className="text-sm text-rose-700">{err || 'No data'}</p></DialogContent>
    </Dialog>
  );

  const { student, lead, batches, invoices, invoice_totals, audit } = data;
  const lead_url = lead.lead_id ? `https://crm.zoho.com/crm/org${process.env.REACT_APP_ZOHO_CRM_ORG_ID || '690252559'}/tab/Leads/${lead.lead_id}` : null;
  const dqIssues = ['placed_company_name', 'placement_date', 'placement_company_job_title', 'placed_company_ctc']
    .filter(k => !lead[k] && (lead.placement_status === 'Done' || lead.placement_status === 'Placed'));
  // Active/most-recent trainer (first batch is the running/active one per backend sort)
  const activeBatch = batches.find(b => (b.batch_status || '').toLowerCase().includes('running')) || batches[0];
  const activeTrainer = activeBatch?.trainer_name;
  const lowAttWarning = student.low_attendance_warning;

  const handleManualFlag = async () => {
    const msg = lowAttWarning
      ? `Flag ${student.name} as Fit_to_Placement = Yes in Zoho CRM?\n\n⚠️ Low attendance: ${student.active_attendance_pct}% (below 70%). Auto-flag would have skipped this. Proceed manually?`
      : `Flag ${student.name} as Fit_to_Placement = Yes in Zoho CRM?`;
    if (!window.confirm(msg)) return;
    setFlagging(true);
    try {
      const res = await api.post(`/placement/manual-flag/${studentId}`);
      if (res.data?.status === 'success') {
        toast.success(`${student.name} flagged as Fit ✓`);
        // reload candidate
        const r = await api.get(`/placement/candidate/${studentId}`);
        setData(r.data);
      } else {
        toast.error(`Failed: ${res.data?.message || 'CRM update failed'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to flag');
    } finally { setFlagging(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0" data-testid={`candidate-drilldown-${studentId}`}>
        <DialogHeader className="px-6 pt-5 pb-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
          <DialogTitle className="flex items-baseline gap-2 flex-wrap">
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-mono text-xs">{student.enrollment_no}</span>
            <span>{student.name}</span>
            {student.phone && <span className="text-xs font-normal text-slate-500 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{student.phone}</span>}
            {student.email && <span className="text-xs font-normal text-slate-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{student.email}</span>}
          </DialogTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            <div className="bg-white rounded p-2 border">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Course Status</p>
              <p className="text-xs font-semibold text-slate-800">{student.course_completion_status || '—'}</p>
            </div>
            <div className={`bg-white rounded p-2 border ${activeBatch?.batch_id ? 'cursor-pointer hover:bg-violet-50/40' : ''}`} onClick={() => activeBatch?.batch_id && setTopicDialog({ batch_id: activeBatch.batch_id, batch_label: `#${activeBatch.batch_number} · ${activeBatch.batch_name}` })} data-testid="course-completion-card">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Course Completion</p>
                {activeBatch?.batch_id && <span className="text-[8px] text-violet-600">View topics →</span>}
              </div>
              <PctBar value={student.course_completion_pct} />
              {lowAttWarning && (
                <p className="text-[9px] text-amber-700 mt-1 flex items-center gap-1"><AlertCircle className="w-2.5 h-2.5" />Attendance {student.active_attendance_pct}% &lt; 70% (auto-flag skipped)</p>
              )}
            </div>
            <div className="bg-white rounded p-2 border">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Outstanding</p>
              <p className={`text-xs font-bold tabular-nums ${student.due_amount > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{fmtMoney(student.due_amount)}</p>
            </div>
            <div className="bg-white rounded p-2 border">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">Placement Status</p>
              <p className="text-xs font-semibold text-slate-800">
                {lead.placement_status ? <Badge variant="outline" className="text-[10px]">{lead.placement_status}</Badge> : '—'}
                {lead.fit_to_placement && <Badge className="ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">Fit ✓</Badge>}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            <div className="bg-white/60 rounded p-2 border border-slate-200">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1"><Users className="w-3 h-3" />Salesperson</p>
              <p className="text-xs font-semibold text-slate-800">{student.salesperson || <span className="text-slate-400">—</span>}</p>
            </div>
            <div className="bg-white/60 rounded p-2 border border-slate-200">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1"><GraduationCap className="w-3 h-3" />Active Trainer</p>
              <p className="text-xs font-semibold text-slate-800">{activeTrainer || <span className="text-slate-400">—</span>}</p>
            </div>
            <div className="bg-white/60 rounded p-2 border border-slate-200">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" />Course</p>
              <p className="text-xs font-semibold text-slate-800 leading-tight">{student.course || <span className="text-slate-400">—</span>}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-[11px] items-center">
            {lead_url && <a href={lead_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">Open Lead in CRM<ExternalLink className="w-3 h-3" /></a>}
            <a href={`/training/students?search=${encodeURIComponent(student.enrollment_no)}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">Student profile<ExternalLink className="w-3 h-3" /></a>
            <div className="ml-auto flex items-center gap-2">
              {lead.lead_id && lead.fit_to_placement !== true && (
                <Button size="sm" variant="outline" disabled={flagging} onClick={handleManualFlag} className={`h-7 text-[11px] ${lowAttWarning ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`} data-testid="drilldown-manual-flag">
                  <CheckCircle2 className="w-3 h-3 mr-1" />{flagging ? 'Flagging…' : (lowAttWarning ? 'Override Flag Fit' : 'Flag Fit')}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setTicketOpen(true)} className="h-7 text-[11px]" data-testid="raise-ticket-btn">
                <FileText className="w-3 h-3 mr-1" />Raise Ticket
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <section>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><GraduationCap className="w-3 h-3" />Batch Enrollments ({batches.length})</p>
            {batches.length === 0 ? <p className="text-xs text-slate-400 italic py-2">No batches enrolled.</p> : batches.map((b) => {
              const collapsed = collapsedBatches[b.batch_id];
              const att = b.attendance;
              const attRate = att.held_count > 0 ? Math.round(att.present_count / att.held_count * 100) : null;
              const isRunning = (b.batch_status || '').toLowerCase().includes('running');
              return (
                <Card key={b.batch_id} className={`p-3 mb-2 ${isRunning ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`} data-testid={`drill-batch-${b.batch_number}`}>
                  <button type="button" onClick={() => setCollapsedBatches(c => ({ ...c, [b.batch_id]: !c[b.batch_id] }))} className="w-full flex items-center gap-2 text-left">
                    {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                    <span className="font-mono text-[11px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">#{b.batch_number}</span>
                    <span className="text-sm font-semibold text-slate-800">{b.batch_name}</span>
                    <Badge variant="outline" className="text-[10px]">{b.batch_status}</Badge>
                    {b.batch_type && <span className="text-[10px] text-slate-500">· {b.batch_type}</span>}
                    <span className="ml-auto text-[10px] text-slate-500">{b.course_name} · {b.trainer_name}</span>
                  </button>
                  {!collapsed && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-[11px]">
                      <div className="bg-white rounded p-2 border">
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" />Dates</p>
                        <p className="font-mono">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</p>
                      </div>
                      <div className="bg-white rounded p-2 border cursor-pointer hover:bg-violet-50/40" onClick={() => setTopicDialog({ batch_id: b.batch_id, batch_label: `#${b.batch_number} · ${b.batch_name}` })} data-testid={`batch-topic-card-${b.batch_number}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Topic Completion</p>
                          <span className="text-[8px] text-violet-600">View topics →</span>
                        </div>
                        <PctBar value={b.topic_completion_pct} />
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1"><Users className="w-3 h-3" />Attendance</p>
                        <p className="text-[11px]"><span className="font-bold text-emerald-700">{att.present_count}</span> present / <span className="font-semibold">{att.held_count}</span> held <span className="text-slate-400">(expected {att.expected_count})</span></p>
                        {attRate !== null && <p className="text-[10px] text-slate-500 mt-0.5">{attRate}% present rate</p>}
                        {att.absent_count > 0 && <p className="text-[10px] text-rose-600">{att.absent_count} absent</p>}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </section>

          <section>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><Briefcase className="w-3 h-3" />Placement (from Zoho CRM)
              {lead.last_synced_at && <span className="text-[9px] text-slate-400 font-normal ml-1">· last synced {fmtDate(lead.last_synced_at)}</span>}
            </p>
            <Card className="p-3">
              <table className="w-full text-[11px]">
                <tbody>
                  {[
                    ['Fit to Placement', lead.fit_to_placement === true ? `Yes${lead.fit_to_placement_date ? ` · set ${fmtDate(lead.fit_to_placement_date)}` : ''}` : (lead.fit_to_placement === false ? 'No' : '—')],
                    ['Placement Status', lead.placement_status],
                    ['Placed Company', lead.placed_company_name, dqIssues.includes('placed_company_name')],
                    ['Job Title', lead.placement_company_job_title, dqIssues.includes('placement_company_job_title')],
                    ['CTC', lead.placed_company_ctc ? fmtMoney(lead.placed_company_ctc) : null, dqIssues.includes('placed_company_ctc')],
                    ['Placement Date', fmtDate(lead.placement_date), dqIssues.includes('placement_date')],
                    ['5 Job Opportunities', lead.five_job_opportunities_given],
                    ['Job Basket User', lead.job_basket_user],
                    ['Onboarding Status', lead.job_basket_onboarding_status],
                    ['JB Team Status', lead.status_on_job_basket],
                    ['Communication Issue', lead.communication_issue],
                    ['Technical Issue', lead.technical_knowledge_issues],
                    ['JB Team Remarks', lead.jb_team_remarks],
                    ['Audit by JB Team', lead.audit_from_job_basket_team],
                    ['In Recruit Module', lead.client_is_added_in_recruit ? 'Yes' : 'No'],
                  ].map(([label, value, missing], i) => (
                    <tr key={i} className="border-t border-slate-100 first:border-0">
                      <td className="py-1 pr-2 text-slate-500 w-40">{label}</td>
                      <td className="py-1 text-slate-800">
                        {value || value === 0 ? <span>{value}</span> : <span className="text-slate-300">—</span>}
                        {missing && <Badge variant="outline" className="ml-2 bg-rose-50 text-rose-700 border-rose-200 text-[9px]">data quality</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </section>

          <section>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><Receipt className="w-3 h-3" />Invoices ({invoices.length})</p>
            {invoices.length === 0 ? <p className="text-xs text-slate-400 italic">No invoices.</p> : (
              <Card className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Invoice</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Date</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Total</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Paid</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Balance</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Due</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Exp. Payment</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-600">Status</th>
                      <th className="px-2 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono text-[10px]">{inv.invoice_number}</td>
                        <td className="px-2 py-1.5 font-mono">{fmtDate(inv.date)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(inv.total)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{fmtMoney(inv.paid)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${inv.balance > 0 ? 'text-rose-700' : 'text-slate-400'}`}>{fmtMoney(inv.balance)}</td>
                        <td className="px-2 py-1.5 font-mono">{fmtDate(inv.due_date)}</td>
                        <td className="px-2 py-1.5 font-mono">{fmtDate(inv.expected_payment_date)}</td>
                        <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{inv.status}</Badge></td>
                        <td className="px-2 py-1.5">
                          {booksInvoiceLink(inv.invoice_id) && (
                            <a href={booksInvoiceLink(inv.invoice_id)} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline text-[10px] inline-flex items-center gap-1">Open<ExternalLink className="w-3 h-3" /></a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t">
                    <tr>
                      <td colSpan={2} className="px-2 py-1.5 font-bold text-slate-700">Totals</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmtMoney(invoice_totals.total)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-700">{fmtMoney(invoice_totals.paid)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold text-rose-700">{fmtMoney(invoice_totals.balance)}</td>
                      <td colSpan={4}></td>
                    </tr>
                  </tfoot>
                </table>
              </Card>
            )}
          </section>

          {audit.length > 0 && (
            <section>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><FileText className="w-3 h-3" />Placement Audit Trail ({audit.length})</p>
              <Card className="p-3 space-y-2">
                {audit.map((a, i) => (
                  <div key={i} className="text-[11px] flex items-start gap-2 border-l-2 pl-2 py-1" style={{ borderColor: a.status === 'success' ? '#10b981' : '#f43f5e' }}>
                    <span className="font-mono text-[10px] text-slate-500 shrink-0 w-24">{fmtDate(a.set_at)}</span>
                    <span className="flex-1">
                      <Badge variant="outline" className="text-[9px] mr-1">{a.action}</Badge>
                      <span className="text-slate-700">by <strong>{a.set_by}</strong> · trigger <em>{a.trigger_source}</em></span>
                      <p className="text-[10px] text-slate-500 mt-0.5">Topic {a.topic_completion_pct}% · Attendance {a.attendance_pct}% · batch #{a.batch_number}</p>
                    </span>
                    <Badge variant="outline" className={`text-[9px] ${a.status === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>{a.status}</Badge>
                  </div>
                ))}
              </Card>
            </section>
          )}
        </div>

        <div className="px-6 py-3 border-t bg-slate-50 flex justify-end">
          <Button size="sm" onClick={onClose} className="text-xs">Close</Button>
        </div>
      </DialogContent>
      {ticketOpen && <RaiseTicketDialog student={student} onClose={() => setTicketOpen(false)} />}
      {topicDialog && <TopicProgressDialog batchId={topicDialog.batch_id} batchLabel={topicDialog.batch_label} studentId={studentId} onClose={() => setTopicDialog(null)} />}
    </Dialog>
  );
}

function TopicProgressDialog({ batchId, batchLabel, studentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/placement/topic-progress/${batchId}?student_id=${studentId}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load topics'))
      .finally(() => setLoading(false));
  }, [batchId, studentId]);
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0" data-testid="topic-progress-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-baseline gap-2 flex-wrap">
            <GraduationCap className="w-4 h-4 text-violet-500" />
            <span>Topics Done</span>
            <span className="text-xs font-normal text-slate-500">{batchLabel}</span>
          </DialogTitle>
          {!loading && data && (
            <div className="flex items-center gap-3 mt-2 text-[11px]">
              <span><strong className="text-emerald-700 tabular-nums">{data.covered_count}</strong> / {data.syllabus_count} topics covered</span>
              <span className="text-slate-400">·</span>
              <span className="font-semibold text-slate-700">{data.overall_pct}%</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500">{data.course_name}</span>
              <div className="ml-auto inline-flex items-center gap-2">
                <label className="text-[10px] text-slate-500 inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="rounded" />Show pending topics
                </label>
              </div>
            </div>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? <p className="text-xs italic text-slate-400 py-6 text-center">Loading topics…</p> : data?.modules?.length === 0 ? (
            <p className="text-xs italic text-slate-400 py-6 text-center">No syllabus linked for this batch.</p>
          ) : data?.modules?.map((mod, mi) => (
            <div key={mi} className="mb-4">
              <div className="flex items-center justify-between mb-2 px-2">
                <p className="text-xs font-semibold text-slate-800">{mod.name}</p>
                <span className="text-[10px] text-slate-500">{mod.done}/{mod.total} · {mod.pct}%</span>
              </div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-1.5 text-[9px] uppercase font-semibold text-slate-500">#</th>
                      <th className="text-left px-3 py-1.5 text-[9px] uppercase font-semibold text-slate-500">Topic</th>
                      <th className="text-left px-3 py-1.5 text-[9px] uppercase font-semibold text-slate-500">Status</th>
                      <th className="text-left px-3 py-1.5 text-[9px] uppercase font-semibold text-slate-500">Date</th>
                      <th className="text-left px-3 py-1.5 text-[9px] uppercase font-semibold text-slate-500">Student Att.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAll ? mod.subtopics : mod.subtopics.filter(s => s.completed)).map((s, si) => (
                      <tr key={si} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 text-slate-400 tabular-nums">{si + 1}</td>
                        <td className="px-3 py-1.5">{s.name}</td>
                        <td className="px-3 py-1.5">
                          {s.completed
                            ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Covered</Badge>
                            : <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">Pending</Badge>}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">{s.completed_at ? fmtDate(s.completed_at) : '—'}</td>
                        <td className="px-3 py-1.5">
                          {s.student_attendance ? (
                            <Badge variant="outline" className={`text-[9px] ${s.student_attendance === 'present' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s.student_attendance === 'absent' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{s.student_attendance}</Badge>
                          ) : <span className="text-slate-300 text-[10px]">—</span>}
                        </td>
                      </tr>
                    ))}
                    {(showAll ? mod.subtopics : mod.subtopics.filter(s => s.completed)).length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-3 text-center italic text-slate-400">{showAll ? 'No topics in this module' : 'No topics covered yet in this module'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t bg-slate-50 flex justify-end">
          <Button size="sm" onClick={onClose} className="text-xs">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RaiseTicketDialog({ student, onClose }) {
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState('');
  const [rule, setRule] = useState('Placement follow-up');
  const [desc, setDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data.users || r.data || [])).catch(() => {});
  }, []);
  const submit = async () => {
    if (!assignee) { toast.error('Select an assignee'); return; }
    setSubmitting(true);
    try {
      await api.post('/training/reports/tickets', {
        student_id: student.id,
        assigned_to: assignee,
        description: desc,
        rule,
      });
      toast.success('Ticket raised');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to raise ticket');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="raise-ticket-dialog">
        <DialogHeader><DialogTitle>Raise Placement Ticket</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded p-2 text-[11px]">
            <p><span className="text-slate-500">For:</span> <strong>{student.name}</strong> · <span className="font-mono">{student.enrollment_no}</span></p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Rule / Reason</label>
            <select value={rule} onChange={e => setRule(e.target.value)} className="w-full h-9 border rounded px-2 text-xs" data-testid="ticket-rule">
              <option>Placement follow-up</option>
              <option>Missing CRM placement details</option>
              <option>5 Job Opportunities pending</option>
              <option>Course progress concern</option>
              <option>Outstanding fees blocker</option>
              <option>JB Team escalation</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Assignee</label>
            <select value={assignee} onChange={e => setAssignee(e.target.value)} className="w-full h-9 border rounded px-2 text-xs" data-testid="ticket-assignee">
              <option value="">Select assignee…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Description</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Add context for the assignee…" className="w-full border rounded p-2 text-xs min-h-[80px]" data-testid="ticket-desc" />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={onClose} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting} className="text-xs bg-violet-600 hover:bg-violet-700" data-testid="ticket-submit">{submitting ? 'Submitting…' : 'Raise Ticket'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
